import {
  computed,
  defineComponent,
  h,
  nextTick,
  onUnmounted,
  reactive,
  ref,
  shallowRef,
  toRaw,
  unref,
  watch,
  watchEffect
} from 'vue';

import { classNames, keyNames, pickAttrs, warn } from '@components/utils';

import { makeTreeEventsProps, makeTreeProps, useMaxLevel } from '../composable';
import { makeDisabledProps, useRender } from '../../../composable';
import {
  arrAdd,
  arrDel,
  calcDropPosition,
  calcSelectedKeys,
  conductCheck,
  conductExpandParent,
  convertDataToEntities,
  convertNodePropsToEventData,
  convertTreeToData,
  fillFieldNames,
  flattenTreeData,
  getDragChildrenKeys,
  getTreeNodeProps,
  parseCheckedKeys,
  posToArr
} from '../utils';

import DropIndicator from './DropIndicator';
import NodeList, { MOTION_KEY, MotionEntity } from './NodeList';
import { TreeContext, useProvideKeysState } from './TreeContext';

import type { ScrollTo } from './VirtualList';
import type { PropType } from 'vue';
import type { NTree } from '../interface';

const MAX_RETRY_TIMES = 10;

export default defineComponent({
  name: 'TreeProvider',
  inheritAttrs: false,
  props: {
    ...makeTreeProps(),
    ...makeDisabledProps(),
    ...makeTreeEventsProps(),

    // Internal props
    children: [Object, Array] as PropType<any>,
    activeKey: {
      type: [Number, String] as PropType<NTree.TKey>
    },
    focusable: {
      type: Boolean as PropType<boolean>,
      default: undefined
    },
    tabindex: {
      type: Number as PropType<number>,
      default: undefined
    },
    defaultCheckedKeys: {
      type: Array as PropType<NTree.TKey[]>,
      default: () => []
    },
    dropIndicatorRender: {
      type: Function as PropType<
        (props: { dropPosition: -1 | 0 | 1; dropLevelOffset: number; indent: number; prefixCls: string }) => any
      >,
      default: DropIndicator
    },
    itemHeight: {
      type: Number as PropType<number>,
      default: 28
    },
    replaceFields: {
      type: Object as PropType<NTree.IFieldNames>,
      default: undefined
    }
  },
  setup(props, { attrs, slots, expose }) {
    const destroyed = ref(false);
    const indent = ref<number | undefined>(undefined);
    const selectedKeys = shallowRef<NTree.TKey[] | undefined>([]);
    const checkedKeys = shallowRef<NTree.TKey[]>([]);
    const halfCheckedKeys = shallowRef<NTree.TKey[]>([]);
    const loadedKeys = shallowRef<NTree.TKey[]>([]);
    const loadingKeys = shallowRef<NTree.TKey[]>([]);
    const expandedKeys = shallowRef<NTree.TKey[]>([]);

    const loadingRetryTimes: Record<NTree.TKey, number> = {};

    const treeData = shallowRef<NTree.IDataNode[]>([]);
    const keyEntities = shallowRef<Record<NTree.TKey, NTree.IDataEntity<any>>>({});

    const focused = ref(false);
    const activeKey = ref<NTree.TKey | null>(null);

    const listChanging = ref(false);
    const fieldNames = computed(() => fillFieldNames(props.fieldNames));

    const listRef = ref();

    let delayedDragEnterLogic: Record<NTree.TKey, number> = {};
    let dragStartMousePosition: { x: number; y: number } | null = null;
    let dragNode: NTree.TDragNodeEvent | null = null;
    let currentMouseOverDroppableNodeKey: NTree.TKey | null = null;

    const dragState = reactive<NTree.IDragState>({
      draggingNodeKey: null,
      dragChildrenKeys: [],

      // dropTargetKey is the key of abstract-drop-node
      // the abstract-drop-node is the real drop node when drag and drop
      // not the DOM drag over node
      dropTargetKey: null,
      dropPosition: null, // the drop position of abstract-drop-node, inside 0, top -1, bottom 1
      dropContainerKey: null, // the container key of abstract-drop-node if dropPosition is -1 or 1
      dropLevelOffset: null, // the drop level offset of abstract-drag-over-node
      dropTargetPos: null, // the pos of abstract-drop-node
      dropAllowed: true, // if drop to abstract-drop-node is allowed
      // the abstract-drag-over-node
      // if mouse is on the bottom of top dom node or no the top of the bottom dom node
      // abstract-drag-over-node is the top node
      dragOverNodeKey: null
    });

    const expandedKeysSet = computed(() => {
      return new Set(unref(expandedKeys));
    });
    const selectedKeysSet = computed(() => {
      return new Set(unref(selectedKeys));
    });
    const loadedKeysSet = computed(() => {
      return new Set(unref(loadedKeys));
    });
    const loadingKeysSet = computed(() => {
      return new Set(unref(loadingKeys));
    });
    const checkedKeysSet = computed(() => {
      return new Set(unref(checkedKeys));
    });
    const halfCheckedKeysSet = computed(() => {
      return new Set(unref(halfCheckedKeys));
    });

    const treeNodeRequiredProps = computed<NTree.ITreeNodeRequiredProps>(() => {
      return {
        expandedKeysSet: unref(expandedKeysSet),
        selectedKeysSet: unref(selectedKeysSet),
        loadedKeysSet: unref(loadedKeysSet),
        loadingKeysSet: unref(loadingKeysSet),
        checkedKeysSet: unref(checkedKeysSet),
        halfCheckedKeysSet: unref(halfCheckedKeysSet),
        dragOverNodeKey: dragState.dragOverNodeKey,
        dropPosition: dragState.dropPosition,
        keyEntities: unref(keyEntities)
      };
    });

    const activeItem = computed<NTree.IFlattenNode | null>(() => {
      if (activeKey.value === null) {
        return null;
      }

      return flattenNodes.value.find(({ key }) => key === activeKey.value) || null;
    });

    watch(
      [() => props.treeData, () => props.children],
      () => {
        treeData.value =
          props.treeData !== undefined ? toRaw(props.treeData).slice() : convertTreeToData(toRaw(props.children));
      },
      { immediate: true, deep: true }
    );

    watchEffect(() => {
      if (unref(treeData)) {
        const entitiesMap = convertDataToEntities(unref(treeData), {
          fieldNames: unref(fieldNames)
        });

        keyEntities.value = {
          [MOTION_KEY]: MotionEntity,
          ...entitiesMap.keyEntities
        };
      }
    });

    let init = false;

    watch(
      [() => props.expandedKeys, () => props.autoExpandParent, keyEntities],
      ([_newKeys, newAutoExpandParent], [_oldKey, oldAutoExpandParent]) => {
        let keys = unref(expandedKeys);

        if (props.expandedKeys !== undefined || (init && newAutoExpandParent !== oldAutoExpandParent)) {
          keys =
            props.autoExpandParent || (!init && props.defaultExpandParent)
              ? conductExpandParent(props.expandedKeys!, unref(keyEntities))
              : props.expandedKeys!;
        } else if (!init && props.defaultExpandAll) {
          const cloneKeyEntities: Record<string, any> = { ...unref(keyEntities) };
          delete cloneKeyEntities[MOTION_KEY];
          keys = Object.keys(cloneKeyEntities).map((key) => cloneKeyEntities[key].key);
        } else if (!init && props.defaultExpandedKeys) {
          keys =
            props.autoExpandParent || props.defaultExpandParent
              ? conductExpandParent(props.defaultExpandedKeys, unref(keyEntities))
              : props.defaultExpandedKeys;
        }

        if (keys) {
          expandedKeys.value = keys;
        }

        init = true;
      },
      { immediate: true }
    );

    // ============================ flattenNodes =============================
    const flattenNodes = shallowRef<NTree.IFlattenNode[]>([]);
    watchEffect(() => {
      flattenNodes.value = flattenTreeData(unref(treeData), unref(expandedKeys), unref(fieldNames));
    });
    // ============================ flattenNodes =============================

    // ============================ selectedKeys =============================
    watchEffect(() => {
      if (props.selectable) {
        if (props.selectedKeys !== undefined) {
          selectedKeys.value = calcSelectedKeys(props.selectedKeys, props);
        } else if (!init && props.defaultSelectedKeys) {
          selectedKeys.value = calcSelectedKeys(props.defaultSelectedKeys, props);
        }
      }
    });

    const { maxLevel, levelEntities } = useMaxLevel(keyEntities);
    // ============================ selectedKeys =============================

    // ============================ checkedKeys ==============================
    watchEffect(() => {
      if (props.checkable) {
        let checkedKeyEntity;

        if (props.checkedKeys !== undefined) {
          checkedKeyEntity = parseCheckedKeys(props.checkedKeys) || {};
        } else if (!init && props.defaultCheckedKeys) {
          checkedKeyEntity = parseCheckedKeys(props.defaultCheckedKeys) || {};
        } else if (unref(treeData)) {
          checkedKeyEntity = parseCheckedKeys(props.checkedKeys!) || {
            checkedKeys: unref(checkedKeys),
            halfCheckedKeys: unref(halfCheckedKeys)
          };
        }

        if (checkedKeyEntity) {
          let { checkedKeys: newCheckedKeys = [], halfCheckedKeys: newHalfCheckedKeys = [] } = checkedKeyEntity;

          if (!props.checkStrictly) {
            const conductKeys = conductCheck(
              newCheckedKeys,
              true,
              unref(keyEntities),
              unref(maxLevel),
              unref(levelEntities)!
            );
            ({ checkedKeys: newCheckedKeys, halfCheckedKeys: newHalfCheckedKeys } = conductKeys);
          }

          checkedKeys.value = newCheckedKeys;
          halfCheckedKeys.value = newHalfCheckedKeys;
        }
      }
    });
    // ============================ checkedKeys ==============================

    // ============================ loadedKeys ===============================
    watchEffect(() => {
      if (props.loadedKeys) {
        loadingKeys.value = props.loadedKeys;
      }
    });

    const resetDragState = () => {
      Object.assign(dragState, {
        dragOverNodeKey: null,
        dropPosition: null,
        dropLevelOffset: null,
        dropTargetKey: null,
        dropContainerKey: null,
        dropTargetPos: null,
        dropAllowed: false
      });
    };

    const scrollTo: ScrollTo = (scroll) => {
      listRef.value.scrollTo(scroll);
    };

    watch(
      () => props.activeKey,
      () => {
        if (props.activeKey !== undefined) {
          activeKey.value = props.activeKey;
        }
      },
      { immediate: true }
    );

    watch(
      activeKey,
      (val) => {
        nextTick(() => {
          if (val !== null) {
            scrollTo({ key: val });
          }
        });
      },
      { immediate: true, flush: 'post' }
    );
    // ============================ loadedKeys ===============================

    // ============================ Expanded =================================
    /** Set uncontrolled `expandedKeys`. This will also auto update `flattenNodes`. */
    const setExpandedKeys = (keys: NTree.TKey[]) => {
      if (props.expandedKeys === undefined) {
        expandedKeys.value = keys;
      }
    };
    // ============================ Expanded =================================

    const cleanDragState = () => {
      if (dragState.draggingNodeKey !== null) {
        Object.assign(dragState, {
          draggingNodeKey: null,
          dropPosition: null,
          dropContainerKey: null,
          dropTargetKey: null,
          dropLevelOffset: null,
          dropAllowed: true,
          dragOverNodeKey: null
        });
      }

      dragStartMousePosition = null;
      currentMouseOverDroppableNodeKey = null;
    };

    // if onNodeDragEnd is called, onWindowDragEnd won't be called since stopPropagation() is called
    const onNodeDragEnd: NTree.TNodeDragEventHandler = (event, node) => {
      const { onDragend } = props;

      dragState.dragOverNodeKey = null;

      cleanDragState();

      onDragend?.({ event, node: node!.eventData });

      dragNode = null;
    };

    // since stopPropagation() is called in treeNode
    // if onWindowDrag is called, whice means state is keeped, drag state should be cleared
    const onWindowDragEnd = (event: DragEvent) => {
      onNodeDragEnd(event, null, true);
      window.removeEventListener('dragend', onWindowDragEnd);
    };

    const onNodeDragStart: NTree.TNodeDragEventHandler = (event, node) => {
      const { onDragstart } = props;
      const { eventKey, eventData } = node!;
      dragNode = node;
      dragStartMousePosition = {
        x: event.clientX,
        y: event.clientY
      };

      const newExpandedKeys = arrDel(unref(expandedKeys), eventKey);

      dragState.draggingNodeKey = eventKey;
      dragState.dragChildrenKeys = getDragChildrenKeys(eventKey, unref(keyEntities));
      indent.value = unref(listRef).getIndentWidth();

      setExpandedKeys(newExpandedKeys);
      window.addEventListener('dragend', onWindowDragEnd);

      if (onDragstart) {
        onDragstart({ event, node: eventData });
      }
    };

    const onNodeDragEnter = (event: DragEvent, node: NTree.TDragNodeEvent) => {
      const { onDragenter, onExpand, allowDrop } = props;
      const { pos, eventKey } = node;
      // record the key of node which is latest entered, used in dragleave event.
      if (currentMouseOverDroppableNodeKey !== eventKey) {
        currentMouseOverDroppableNodeKey = eventKey;
      }

      if (!dragNode) {
        resetDragState();
        return;
      }
      const {
        dropPosition,
        dropLevelOffset,
        dropTargetKey,
        dropContainerKey,
        dropTargetPos,
        dropAllowed,
        dragOverNodeKey
      } = calcDropPosition(
        event,
        dragNode,
        node,
        unref(indent)!,
        dragStartMousePosition!,
        allowDrop,
        unref(flattenNodes),
        unref(keyEntities),
        unref(expandedKeysSet)
      );

      if (
        // don't allow drop inside its children
        dragState.dragChildrenKeys.indexOf(dropTargetKey!) !== -1 ||
        // don't allow drop when drop is not allowed caculated by calcDropPosition
        !dropAllowed
      ) {
        resetDragState();
        return;
      }

      // Side effect for delay drag
      if (!delayedDragEnterLogic) {
        delayedDragEnterLogic = {};
      }
      Object.keys(delayedDragEnterLogic).forEach((key) => {
        clearTimeout(delayedDragEnterLogic[key]);
      });

      if (dragNode.eventKey !== node.eventKey) {
        // hoist expand logic here
        // since if logic is on the bottom
        // it will be blocked by abstract dragover node check
        //   => if you dragenter from top, you mouse will still be consider as in the top node
        delayedDragEnterLogic[pos] = window.setTimeout(() => {
          if (dragState.draggingNodeKey === null) return;

          let newExpandedKeys = unref(expandedKeys).slice();
          const entity = unref(keyEntities)[node.eventKey];

          if (entity && (entity.children || []).length) {
            newExpandedKeys = arrAdd(unref(expandedKeys), node.eventKey);
          }
          setExpandedKeys(newExpandedKeys);

          if (onExpand) {
            onExpand(newExpandedKeys, {
              node: node.eventData,
              expanded: true,
              nativeEvent: event
            });
          }
        }, 800);
      }

      // Skip if drag node is self
      if (dragNode.eventKey === dropTargetKey && dropLevelOffset === 0) {
        resetDragState();
        return;
      }

      // Update drag over node and drag state
      Object.assign(dragState, {
        dragOverNodeKey,
        dropPosition,
        dropLevelOffset,
        dropTargetKey,
        dropContainerKey,
        dropTargetPos,
        dropAllowed
      });

      if (onDragenter) {
        onDragenter({
          event,
          node: node.eventData,
          expandedKeys: unref(expandedKeys)!
        });
      }
    };

    const onNodeDragOver = (event: DragEvent, node: NTree.TDragNodeEvent) => {
      const { onDragover, allowDrop } = props;

      if (!dragNode) {
        return;
      }
      const {
        dropPosition,
        dropLevelOffset,
        dropTargetKey,
        dropContainerKey,
        dropAllowed,
        dropTargetPos,
        dragOverNodeKey
      } = calcDropPosition(
        event,
        dragNode,
        node,
        unref(indent) || 0,
        dragStartMousePosition!,
        allowDrop,
        flattenNodes.value,
        keyEntities.value,
        expandedKeysSet.value
      );

      if (dragState.dragChildrenKeys.indexOf(dropTargetKey!) !== -1 || !dropAllowed) {
        // don't allow drop inside its children
        // don't allow drop when drop is not allowed caculated by calcDropPosition
        return;
      }

      // Update drag position

      if (dragNode.eventKey === dropTargetKey && dropLevelOffset === 0) {
        if (
          !(
            dragState.dropPosition === null &&
            dragState.dropLevelOffset === null &&
            dragState.dropTargetKey === null &&
            dragState.dropContainerKey === null &&
            dragState.dropTargetPos === null &&
            dragState.dropAllowed === false &&
            dragState.dragOverNodeKey === null
          )
        ) {
          resetDragState();
        }
      } else if (
        !(
          dropPosition === dragState.dropPosition &&
          dropLevelOffset === dragState.dropLevelOffset &&
          dropTargetKey === dragState.dropTargetKey &&
          dropContainerKey === dragState.dropContainerKey &&
          dropTargetPos === dragState.dropTargetPos &&
          dropAllowed === dragState.dropAllowed &&
          dragOverNodeKey === dragState.dragOverNodeKey
        )
      ) {
        Object.assign(dragState, {
          dropPosition,
          dropLevelOffset,
          dropTargetKey,
          dropContainerKey,
          dropTargetPos,
          dropAllowed,
          dragOverNodeKey
        });
      }

      if (onDragover) {
        onDragover({ event, node: node.eventData });
      }
    };

    const onNodeDragLeave: NTree.TNodeDragEventHandler = (event, node) => {
      // if it is outside the droppable area
      // currentMouseOverDroppableNodeKey will be updated in dragenter event when into another droppable receiver.
      if (
        currentMouseOverDroppableNodeKey === node!.eventKey &&
        !(event.currentTarget as any).contains(event.relatedTarget as Node)
      ) {
        resetDragState();
        currentMouseOverDroppableNodeKey = null;
      }

      const { onDragleave } = props;

      if (onDragleave) {
        onDragleave({ event, node: node!.eventData });
      }
    };

    const onNodeDrop = (event: DragEvent, _node: any, outsideTree = false) => {
      const { dragChildrenKeys, dropPosition, dropTargetKey, dropTargetPos, dropAllowed } = dragState;

      if (!dropAllowed) return;

      const { onDrop } = props;

      dragState.dragOverNodeKey = null;
      cleanDragState();

      if (dropTargetKey === null) return;

      const abstractDropNodeProps = {
        ...getTreeNodeProps(dropTargetKey, toRaw(unref(treeNodeRequiredProps))),
        active: unref(activeItem)?.key === dropTargetKey,
        data: unref(keyEntities)[dropTargetKey].node
      };
      const dropToChild = dragChildrenKeys.indexOf(dropTargetKey) !== -1;

      if (dropToChild) {
        warn(
          'CdsTree',
          "Can not drop to dragNode's children node. Maybe this is a bug of CDS. Please report an issue."
        );
      }

      const posArr = posToArr(dropTargetPos!);

      const dropResult = {
        event,
        node: convertNodePropsToEventData(abstractDropNodeProps),
        dragNode: dragNode ? dragNode.eventData : null,
        dragNodesKeys: [dragNode!.eventKey].concat(dragChildrenKeys),
        dropToGap: dropPosition !== 0,
        dropPosition: dropPosition! + Number(posArr[posArr.length - 1])
      };

      if (!outsideTree) {
        onDrop?.(dropResult);
      }

      dragNode = null;
    };

    const onNodeClick: NTree.TNodeMouseEventHandler = (e, treeNode) => {
      const { onClick } = props;
      if (onClick) {
        onClick(e, treeNode);
      }
    };

    const onNodeDoubleClick: NTree.TNodeMouseEventHandler = (e, treeNode) => {
      const { onDblclick } = props;
      if (onDblclick) {
        onDblclick(e, treeNode);
      }
    };

    const onNodeSelect: NTree.TNodeMouseEventHandler = (e, treeNode) => {
      let newSelectedKeys = unref(selectedKeys);
      const { onSelect, multiple } = props;
      const { selected } = treeNode;
      const key = treeNode[fieldNames.value.key];
      const targetSelected = !selected;

      // Update selected keys
      if (!targetSelected) {
        newSelectedKeys = arrDel(newSelectedKeys!, key);
      } else if (!multiple) {
        newSelectedKeys = [key];
      } else {
        newSelectedKeys = arrAdd(newSelectedKeys!, key);
      }

      // [Legacy] Not found related usage in doc or upper libs
      const keyEntitiesValue = unref(keyEntities);
      const selectedNodes = newSelectedKeys
        .map((selectedKey) => {
          const entity = keyEntitiesValue[selectedKey];
          if (!entity) return null;

          return entity.node;
        })
        .filter((node) => node);

      if (props.selectedKeys === undefined) {
        selectedKeys.value = newSelectedKeys;
      }

      if (onSelect) {
        onSelect(newSelectedKeys, {
          event: 'select',
          selected: targetSelected,
          node: treeNode,
          selectedNodes,
          nativeEvent: e
        });
      }
    };

    const onNodeCheck = (e: MouseEvent, treeNode: NTree.IEventDataNode, checked: boolean) => {
      const { checkStrictly, onCheck } = props;
      const key = treeNode[unref(fieldNames).key];

      // Prepare trigger arguments
      let checkedObj;
      const eventObj: Partial<NTree.ICheckInfo> = {
        event: 'check',
        node: treeNode,
        checked,
        nativeEvent: e
      };
      const keyEntitiesValue = unref(keyEntities);

      if (checkStrictly) {
        const newCheckedKeys = checked ? arrAdd(unref(checkedKeys), key) : arrDel(unref(checkedKeys), key);
        const newHalfCheckedKeys = arrDel(unref(halfCheckedKeys), key);
        checkedObj = { checked: newCheckedKeys, halfChecked: newHalfCheckedKeys };

        eventObj.checkedNodes = newCheckedKeys
          .map((checkedKey) => keyEntitiesValue[checkedKey])
          .filter((entity) => entity)
          .map((entity) => entity.node);

        if (props.checkedKeys === undefined) {
          checkedKeys.value = newCheckedKeys;
        }
      } else {
        // Always fill first
        let { checkedKeys: newCheckedKeys, halfCheckedKeys: newHalfCheckedKeys } = conductCheck(
          [...unref(checkedKeys), key],
          true,
          keyEntitiesValue,
          unref(maxLevel),
          unref(levelEntities)!
        );

        // If remove, we do it again to correction
        if (!checked) {
          const keySet = new Set(newCheckedKeys);
          keySet.delete(key);
          ({ checkedKeys: newCheckedKeys, halfCheckedKeys: newHalfCheckedKeys } = conductCheck(
            Array.from(keySet),
            { checked: false, halfCheckedKeys: newHalfCheckedKeys },
            keyEntitiesValue,
            unref(maxLevel),
            unref(levelEntities)!
          ));
        }

        checkedObj = newCheckedKeys;

        // [Legacy] This is used for vc-tree-select`
        eventObj.checkedNodes = [];
        eventObj.checkedNodesPositions = [];
        eventObj.halfCheckedKeys = newHalfCheckedKeys;
        newCheckedKeys.forEach((checkedKey) => {
          const entity = keyEntitiesValue[checkedKey];
          if (!entity) return;

          const { node, pos } = entity;
          eventObj.checkedNodes!.push(node);
          eventObj.checkedNodesPositions!.push({ node, pos });
        });
        if (props.checkedKeys === undefined) {
          checkedKeys.value = newCheckedKeys;
          halfCheckedKeys.value = newHalfCheckedKeys;
        }
      }

      if (onCheck) {
        onCheck(checkedObj, eventObj as NTree.ICheckInfo);
      }
    };

    const onNodeLoad = (treeNode: NTree.IEventDataNode) => {
      const key = treeNode[fieldNames.value.key];
      const loadPromise = new Promise<void>((resolve, reject) => {
        // We need to get the latest state of loading/loaded keys
        const { loadData, onLoad } = props;

        if (!loadData || unref(loadedKeysSet).has(key) || unref(loadingKeysSet).has(key)) {
          return null;
        }

        // Process load data
        const promise = loadData(treeNode);
        promise
          .then(() => {
            const newLoadedKeys = arrAdd(unref(loadedKeys), key);
            const newLoadingKeys = arrDel(unref(loadingKeys), key);

            // onLoad should trigger before internal setState to avoid `loadData` trigger twice.
            if (onLoad) {
              onLoad(newLoadedKeys, {
                event: 'load',
                node: treeNode
              });
            }

            if (props.loadedKeys === undefined) {
              loadedKeys.value = newLoadedKeys;
            }
            loadingKeys.value = newLoadingKeys;
            resolve();
          })
          .catch((e) => {
            loadingKeys.value = arrDel(loadingKeys.value, key);

            // If exceed max retry times, we give up retry
            loadingRetryTimes[key] = (loadingRetryTimes[key] || 0) + 1;
            if (loadingRetryTimes[key] >= MAX_RETRY_TIMES) {
              warn('CdsTree', 'Retry for `loadData` many times but still failed. No more retry.');
              const newLoadedKeys = arrAdd(loadedKeys.value, key);
              if (props.loadedKeys === undefined) {
                loadedKeys.value = newLoadedKeys;
              }
              resolve();
            }

            reject(e);
          });

        loadingKeys.value = arrAdd(loadingKeys.value, key);
      });
      // Not care warning if we ignore this
      loadPromise.catch(() => {});

      return loadPromise;
    };

    const onNodeMouseEnter: NTree.TNodeMouseEventHandler = (event, node) => {
      const { onMouseenter } = props;
      if (onMouseenter) {
        onMouseenter({ event, node });
      }
    };

    const onNodeMouseLeave: NTree.TNodeMouseEventHandler = (event, node) => {
      const { onMouseleave } = props;
      if (onMouseleave) {
        onMouseleave({ event, node });
      }
    };

    const onNodeContextMenu: NTree.TNodeMouseEventHandler = (event, node) => {
      const { onRightClick } = props;
      if (onRightClick) {
        event.preventDefault();
        onRightClick({ event, node });
      }
    };

    const onNodeExpand = (event: MouseEvent, treeNode: NTree.IEventDataNode) => {
      let newExpandedKeys = unref(expandedKeys);
      const { onExpand, loadData } = props;
      const { expanded } = treeNode;
      const key = treeNode[unref(fieldNames).key];

      // Do nothing when motion is in progress
      if (unref(listChanging)) {
        return;
      }

      // Update selected keys
      const index = newExpandedKeys!.indexOf(key);
      const targetExpanded = !expanded;
      const isSync = (expanded && index !== -1) || (!expanded && index === -1);

      if (!isSync) {
        warn('CdsTree', 'Expand state not sync with index check');
      }

      if (targetExpanded) {
        newExpandedKeys = arrAdd(newExpandedKeys!, key);
      } else {
        newExpandedKeys = arrDel(newExpandedKeys!, key);
      }

      setExpandedKeys(newExpandedKeys);

      if (onExpand) {
        onExpand(newExpandedKeys, {
          node: treeNode,
          expanded: targetExpanded,
          nativeEvent: event
        });
      }

      // Async Load data
      if (targetExpanded && loadData) {
        const loadPromise = onNodeLoad(treeNode);
        if (loadPromise) {
          loadPromise
            .then(() => {})
            .catch((e) => {
              const expandedKeysToRestore = arrDel(expandedKeys.value, key);
              setExpandedKeys(expandedKeysToRestore);
              Promise.reject(e);
            });
        }
      }
    };

    const onListChangeStart = () => {
      listChanging.value = true;
    };

    const onListChangeEnd = () => {
      setTimeout(() => {
        listChanging.value = false;
      });
    };

    const onActiveChange = (newActiveKey: NTree.TKey | null) => {
      const { onActiveChange } = props;

      if (unref(activeKey) === newActiveKey) {
        return;
      }
      if (props.activeKey !== undefined) {
        activeKey.value = newActiveKey;
      }
      if (newActiveKey !== null) {
        scrollTo({ key: newActiveKey });
      }

      if (onActiveChange) {
        onActiveChange(newActiveKey);
      }
    };

    const onFocus = (e: FocusEvent) => {
      const { onFocus } = props;

      focused.value = true;

      if (onFocus) {
        onFocus(e);
      }
    };

    const onBlur = (e: FocusEvent) => {
      const { onBlur } = props;

      focused.value = false;
      onActiveChange(null);

      if (onBlur) {
        onBlur(e);
      }
    };

    const offsetActiveKey = (offset: number) => {
      let index = unref(flattenNodes).findIndex(({ key }) => key === unref(activeKey));
      // Align with index
      if (index === -1 && offset < 0) {
        index = unref(flattenNodes).length;
      }

      index = (index + offset + unref(flattenNodes).length) % unref(flattenNodes).length;

      const item = unref(flattenNodes)[index];
      if (item) {
        const { key } = item;
        onActiveChange(key);
      } else {
        onActiveChange(null);
      }
    };

    const activeItemEventNode = computed(() => {
      return convertNodePropsToEventData({
        ...getTreeNodeProps(unref(activeKey)!, treeNodeRequiredProps.value),
        data: unref(activeItem)!.data,
        active: true
      });
    });

    const onKeydown = (event: KeyboardEvent) => {
      const { onKeydown, checkable, selectable } = props;

      // >>>>>>>>>> Direction
      switch (event.key) {
        case keyNames.up: {
          offsetActiveKey(-1);
          event.preventDefault();
          break;
        }
        case keyNames.down: {
          offsetActiveKey(1);
          event.preventDefault();
          break;
        }
      }

      // >>>>>>>>>> Expand & Selection
      const item = unref(activeItem);
      if (item && item.data) {
        const expandable = item.data.isLeaf === false || !!(item.data.children || []).length;
        const eventNode = unref(activeItemEventNode);

        switch (event.key) {
          // >>> Expand
          case keyNames.left: {
            // Collapse if possible
            if (expandable && expandedKeysSet.value.has(unref(activeKey)!)) {
              onNodeExpand({} as MouseEvent, eventNode);
            } else if (item.parent) {
              onActiveChange(item.parent.key);
            }
            event.preventDefault();
            break;
          }
          case keyNames.right: {
            // Expand if possible
            if (expandable && !expandedKeysSet.value.has(unref(activeKey)!)) {
              onNodeExpand({} as MouseEvent, eventNode);
            } else if (item.children && item.children.length) {
              onActiveChange(item.children[0].key);
            }
            event.preventDefault();
            break;
          }

          // Selection
          case keyNames.enter:
          case keyNames.space: {
            if (checkable && !eventNode.disabled && eventNode.checkable !== false && !eventNode.disableCheckbox) {
              onNodeCheck({} as MouseEvent, eventNode, !checkedKeysSet.value.has(unref(activeKey)!));
            } else if (!checkable && selectable && !eventNode.disabled && eventNode.selectable !== false) {
              onNodeSelect({} as MouseEvent, eventNode);
            }
            break;
          }
        }
      }

      if (onKeydown) {
        onKeydown(event);
      }
    };

    useProvideKeysState({
      expandedKeys,
      selectedKeys,
      loadedKeys,
      loadingKeys,
      checkedKeys,
      halfCheckedKeys,
      expandedKeysSet,
      selectedKeysSet,
      loadedKeysSet,
      loadingKeysSet,
      checkedKeysSet,
      halfCheckedKeysSet,
      flattenNodes
    });

    onUnmounted(() => {
      window.removeEventListener('dragend', onWindowDragEnd);
      destroyed.value = true;
    });

    useRender(() => {
      const { draggingNodeKey, dropLevelOffset, dropContainerKey, dropTargetKey, dropPosition, dragOverNodeKey } =
        dragState;

      const {
        showLine,
        focusable,
        tabindex = 0,
        selectable,
        showIcon,
        icon = slots.icon,
        switcherIcon,
        draggable,
        checkable,
        checkStrictly,
        disabled,
        loadData,
        openAnimation,
        filterTreeNode,
        height,
        itemHeight,
        virtual,
        dropIndicatorRender,
        onContextmenu,
        onScroll
      } = props;

      const { class: className, style } = attrs;

      const domProps = pickAttrs({ ...props, ...attrs }, { aria: true, data: true });

      // It's better move to hooks but we just simply keep here
      let draggableConfig: NTree.TDraggableConfig | undefined = undefined;
      if (draggable) {
        if (typeof draggable === 'object') {
          draggableConfig = draggable;
        } else if (typeof draggable === 'function') {
          draggableConfig = {
            nodeDraggable: draggable
          };
        } else {
          draggableConfig = {};
        }
      }

      return h(
        TreeContext,
        {
          value: {
            selectable,
            showIcon,
            icon,
            switcherIcon,
            draggable: draggableConfig,
            draggingNodeKey,
            checkable,
            customCheckable: slots.checkable,
            checkStrictly,
            disabled,
            keyEntities: keyEntities.value,
            dropLevelOffset,
            dropContainerKey,
            dropTargetKey,
            dropPosition,
            dragOverNodeKey,
            dragging: draggingNodeKey !== null,
            indent: indent.value,
            dropIndicatorRender,

            loadData,
            filterTreeNode,

            onNodeClick,
            onNodeDoubleClick,
            onNodeExpand,
            onNodeSelect,
            onNodeCheck,
            onNodeLoad,
            onNodeMouseEnter,
            onNodeMouseLeave,
            onNodeContextMenu,
            onNodeDragStart,
            onNodeDragEnter,
            onNodeDragOver,
            onNodeDragLeave,
            onNodeDragEnd,
            onNodeDrop,
            slots
          } as any
        },
        {
          default: () =>
            h(
              'div',
              {
                role: 'tree',
                class: classNames('cds-tree', className, {
                  ['cds-tree--show-line']: showLine,
                  ['cds-tree--focused']: unref(focused),
                  ['cds-tree--active-focused']: unref(activeKey) !== null
                })
              },
              h(NodeList, {
                ref: listRef,
                style,
                disabled,
                selectable,
                checkable: !!checkable,
                motion: openAnimation,
                virtual,
                height,
                itemHeight,
                focusable,
                focused: unref(focused),
                tabindex,
                activeItem: unref(activeItem),
                onFocus,
                onBlur,
                onKeydown,
                onActiveChange,
                onListChangeStart,
                onListChangeEnd,
                onContextmenu,
                onScroll,
                ...domProps
              })
            )
        }
      );
    });

    expose({
      onNodeExpand,
      scrollTo,
      onKeydown,
      selectedKeys: computed(() => selectedKeys.value),
      checkedKeys: computed(() => checkedKeys.value),
      halfCheckedKeys: computed(() => halfCheckedKeys.value),
      loadedKeys: computed(() => loadedKeys.value),
      loadingKeys: computed(() => loadingKeys.value),
      expandedKeys: computed(() => expandedKeys.value)
    });

    return {};
  }
});
